import axios, { AxiosInstance, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { config } from '../config';
import authService from './auth';
import useAuthStore from '../stores/authStore';
import guestService, { GuestSessionResponse } from './guestService';
import { encodeAdventureIdForPath } from '../utils/adventureIdPath';
import {
  ApiResponse,
  ApiError,
  Adventure,
  AdventureHeader,
  AdventureScene,
  AdventureItem,
  AdventureStat,
  Player,
  PlayerWithUsername,
  GameSession,
  CreateSessionRequest,
  PlayerActionRequest,
  AdventureResponse,
  AdventureHeaderResponse,
  AdventureSceneResponse,
  AdventureItemResponse,
  AdventureStatResponse,
  AdventureValidationResponse,
  AdventureActivitiesResponse,
  SessionResponse,
  PlayerResponse,
  GameStateResponse,
  SceneResponse,
  ActionResponse,
  UserInputActionResponse,
  PlaythroughSnapshotListResult,
  PlaythroughSnapshotEngineRestoreResult,
} from '../types';
import type { RichTextElement } from 'coop-adventure-engine';
import type { SharedVariablesQueryResults } from '../utils/sharedVariablesQueryCoordinator';

class ApiService {
  private axiosInstance: AxiosInstance;
  private isHandlingUnauthorized = false;

  constructor() {
    const apiBaseUrl = config.apiBaseUrl;
    console.log('[ApiService] Initializing with baseURL:', apiBaseUrl);
    console.log('[ApiService] Environment VITE_API_BASE_URL:', import.meta.env.VITE_API_BASE_URL);
    
    this.axiosInstance = axios.create({
      baseURL: apiBaseUrl,
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Test-friendly behavior: when axios is mocked, ensure subsequent axios.create()
    // calls (e.g., in tests) return the same instance so tests can stub requests.
    const maybeMock: any = axios as any;
    if (maybeMock && typeof maybeMock.create === 'function' && maybeMock.create.mockReturnValue) {
      try {
        maybeMock.create.mockReturnValue(this.axiosInstance);
      } catch {
        // no-op if not a mock
      }
    }

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor to add auth token
    this.axiosInstance.interceptors.request.use(
      async (config) => {
        // Special handling for guest session creation endpoint
        // This endpoint uses optionalAuth, so we should only send guest tokens, not regular tokens
        // Check both full URL and relative path
        const urlString = config.url || '';
        const isGuestSessionCreation = urlString.includes('/api/sessions/guest') || 
                                       urlString.includes('/sessions/guest') ||
                                       (config.baseURL && `${config.baseURL}${urlString}`.includes('/api/sessions/guest'));
        const isGuestAdventureRedeem =
          urlString.includes('/api/guest-adventures/redeem') ||
          urlString.includes('/guest-adventures/redeem') ||
          (config.baseURL &&
            `${config.baseURL}${urlString}`.includes('/api/guest-adventures/redeem'));
        
        // Token precedence (documented for maintainability):
        // 1. authService.getCurrentToken() - Highest priority. Holds regular user tokens in memory.
        //    Used immediately after login/register; avoids race with Zustand state updates.
        // 2. authStore (guest) - If no authService token: when isGuest && token, use guest token
        //    from authStore. Guest tokens come from guestService localStorage via createGuestSession.
        // 3. No token - Request proceeds without Authorization (will 401 if endpoint requires auth).
        
        // CRITICAL: Check authService FIRST before checking guest status
        // This ensures that when tokens are set via setTokens() (e.g., after login),
        // they are used immediately, even if Zustand state hasn't updated yet
        const authStateForToken = useAuthStore.getState();
        if (
          !authStateForToken.isGuest &&
          authStateForToken.isSessionRestored &&
          (authService.getCurrentToken() || authService.hasRefreshToken())
        ) {
          // Refresh expired JWT before the request (interval timers are throttled in background tabs)
          await authService.ensureValidToken().catch(() => {});
          const token = authService.getCurrentToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
            return config;
          }
        }
        
        // For guest session creation or invite redeem, don't use regular tokens
        if (isGuestSessionCreation || isGuestAdventureRedeem) {
          return config;
        }
        
        // No token in authService - check if user is a guest and use guest tokens
        const authState = useAuthStore.getState();
        if (authState.isGuest && authState.token) {
          // Try to refresh guest token if needed (don't block request on failure)
          await useAuthStore.getState().ensureValidToken().catch(() => {});
          const updatedAuthState = useAuthStore.getState();
          // Use guest token whenever we have one (avoid 401 when ensureValidToken fails or guestUserId missing)
          if (updatedAuthState.token) {
            config.headers.Authorization = `Bearer ${updatedAuthState.token}`;
            return config;
          }
        }
        
        // Fallback: use guest token from guestService when store has no token but we have guest data
        // (e.g. before auth store rehydration, or store/guestService temporarily out of sync)
        const authStateAgain = useAuthStore.getState();
        if (!authStateAgain.token && !authStateAgain.user) {
          const guestData = guestService.getGuestData();
          if (guestData?.token) {
            config.headers.Authorization = `Bearer ${guestData.token}`;
            return config;
          }
        }
        
        // No token available - request will fail with 401
        // This is expected for unauthenticated requests
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        return response;
      },
      async (error: AxiosError<ApiResponse>) => {
        const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          // Don't trigger logout/redirect for guests - they may be mid-flow (e.g. guest login in adventure).
          const authState = useAuthStore.getState();
          const hasGuestData = !!guestService.getGuestData();
          if (authState.isGuest || hasGuestData) {
            return Promise.reject(this.transformError(error));
          }
          // Don't clear tokens while session restore is in progress (requests may run without token yet).
          if (!authState.isSessionRestored) {
            return Promise.reject(this.transformError(error));
          }

          if (authService.hasRefreshToken()) {
            originalRequest._retry = true;
            try {
              const refreshed = await authService.ensureValidToken();
              const newToken = authService.getCurrentToken();
              if (refreshed && newToken) {
                useAuthStore.setState({ token: newToken });
                originalRequest.headers = originalRequest.headers ?? {};
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                return this.axiosInstance(originalRequest);
              }
            } catch {
              // Refresh failed; fall through to logout
            }
          }

          this.handleUnauthorized();
        }
        return Promise.reject(this.transformError(error));
      }
    );
  }

  private handleUnauthorized(): void {
    // Prevent multiple simultaneous logout handlers
    if (this.isHandlingUnauthorized) {
      return;
    }
    
    this.isHandlingUnauthorized = true;
    
    // Clear authentication state
    authService.logout();
    
    // Dispatch logout event for components to handle
    window.dispatchEvent(new CustomEvent('auth:logout'));
    
    // Reset flag after a short delay to allow proper cleanup
    setTimeout(() => {
      this.isHandlingUnauthorized = false;
    }, 1000);
  }

  private transformError(error: AxiosError<ApiResponse>): ApiError {
    if (error.response?.data?.error) {
      return error.response.data.error;
    }

    if (error.code === 'ECONNABORTED') {
      return {
        code: 'TIMEOUT',
        message: 'Request timeout. Please try again.',
      };
    }

    if (!error.response) {
      // Check if this is likely a certificate/SSL error
      const isHttps = error.config?.url?.startsWith('https://') || error.config?.baseURL?.startsWith('https://');
      const isLocalhost = error.config?.baseURL?.includes('localhost');
      
      if (isHttps && isLocalhost) {
        return {
          code: 'NETWORK_ERROR',
          message: 'Connection blocked. Please accept the self-signed certificate by visiting the server URL directly in your browser first.',
        };
      }
      
      return {
        code: 'NETWORK_ERROR',
        message: 'Network error. Please check your connection.',
      };
    }

    return {
      code: 'UNKNOWN_ERROR',
      message: error.message || 'An unexpected error occurred',
      details: error.response.data,
    };
  }

  // Authentication methods (deprecated - use authService instead)
  setAuthToken(_token: string): void {
    console.warn('setAuthToken is deprecated. Use authService instead.');
  }

  clearAuthToken(): void {
    console.warn('clearAuthToken is deprecated. Use authService instead.');
  }

  private adventureApiPath(adventureId: string, suffix = ''): string {
    const base = `/api/adventures/${encodeAdventureIdForPath(adventureId)}`;
    return suffix ? `${base}/${suffix}` : base;
  }

  // Adventure management
  async listAdventures(tag?: string): Promise<string[]> {
    const url = tag
      ? `/api/adventures?tag=${encodeURIComponent(tag)}`
      : '/api/adventures';
    const response = await this.axiosInstance.get<ApiResponse<{ adventures: string[] }>>(url);
    return response.data.data?.adventures || [];
  }

  // Legacy method - deprecated, use getAdventureHeader instead
  async getAdventure(adventureId: string): Promise<Adventure> {
    const response = await this.axiosInstance.get<ApiResponse<AdventureResponse>>(this.adventureApiPath(adventureId));
    if (!response.data.data?.adventure) {
      throw new Error('Adventure not found');
    }
    return response.data.data.adventure;
  }

  // New refactored methods for individual content access
  async getAdventureHeader(adventureId: string): Promise<AdventureHeader> {
    const response = await this.axiosInstance.get<ApiResponse<AdventureHeaderResponse>>(this.adventureApiPath(adventureId, 'header'));
    if (!response.data.data?.adventure) {
      throw new Error('Adventure header not found');
    }
    return response.data.data.adventure;
  }

  async getAdventureActivities(adventureId: string): Promise<AdventureActivitiesResponse> {
    const response = await this.axiosInstance.get<ApiResponse<AdventureActivitiesResponse>>(
      this.adventureApiPath(adventureId, 'activities')
    );
    if (!response.data.data) {
      throw new Error('Adventure activities not found');
    }
    return response.data.data;
  }

  async getAdventureScene(adventureId: string, sceneId: string): Promise<AdventureScene> {
    const response = await this.axiosInstance.get<ApiResponse<AdventureSceneResponse>>(this.adventureApiPath(adventureId, `scenes/${sceneId}`));
    if (!response.data.data?.scene) {
      throw new Error('Scene not found');
    }
    return response.data.data.scene;
  }

  async getAdventureItem(adventureId: string, itemName: string): Promise<AdventureItem> {
    const response = await this.axiosInstance.get<ApiResponse<AdventureItemResponse>>(this.adventureApiPath(adventureId, `items/${encodeURIComponent(itemName)}`));
    if (!response.data.data?.item) {
      throw new Error('Item not found');
    }
    return response.data.data.item;
  }

  async getItemData(sessionId: string, itemTag: string): Promise<AdventureItem> {
    const response = await this.axiosInstance.get<ApiResponse<AdventureItemResponse>>(`/api/sessions/${sessionId}/items/${encodeURIComponent(itemTag)}`);
    if (!response.data.data?.item) {
      throw new Error('Item not found');
    }
    return response.data.data.item;
  }

  async getPlayerItems(sessionId: string, playerId: string): Promise<AdventureItem[]> {
    const response = await this.axiosInstance.get<ApiResponse<{ items: AdventureItem[] }>>(`/api/sessions/${sessionId}/players/${playerId}/items`);
    if (!response.data.data?.items) {
      throw new Error('Failed to fetch items');
    }
    return response.data.data.items;
  }

  async getAdventureStat(adventureId: string, statName: string): Promise<AdventureStat> {
    const response = await this.axiosInstance.get<ApiResponse<AdventureStatResponse>>(this.adventureApiPath(adventureId, `stats/${encodeURIComponent(statName)}`));
    if (!response.data.data?.stat) {
      throw new Error('Stat not found');
    }
    return response.data.data.stat;
  }

  async validateAdventureContent(adventureId: string): Promise<string[]> {
    const response = await this.axiosInstance.get<ApiResponse<AdventureValidationResponse>>(this.adventureApiPath(adventureId, 'validate'));
    if (!response.data.data) {
      throw new Error('Failed to validate adventure');
    }
    return response.data.data.errors;
  }

  async getScene(adventureId: string, sceneId: string): Promise<{ id: string; description: string; options: any[] }> {
    const response = await this.axiosInstance.get<ApiResponse<{ scene: any }>>(this.adventureApiPath(adventureId, `scenes/${sceneId}`));
    if (!response.data.data?.scene) {
      throw new Error('Scene not found');
    }
    return response.data.data.scene;
  }

  async loadAdventure(adventureId: string, adventureData: string): Promise<Adventure> {
    const response = await this.axiosInstance.post<ApiResponse<AdventureResponse>>('/api/adventures', {
      adventureId,
      adventureData
    });
    if (!response.data.data?.adventure) {
      throw new Error('Failed to load adventure');
    }
    return response.data.data.adventure;
  }

  async validateAdventure(adventureId: string): Promise<string[]> {
    const response = await this.axiosInstance.post<ApiResponse<{ errors: string[] }>>(this.adventureApiPath(adventureId, 'validate'));
    return response.data.data?.errors || [];
  }

  async removeAdventure(adventureId: string): Promise<void> {
    await this.axiosInstance.delete(this.adventureApiPath(adventureId));
  }

  // Session management
  async getSystemSession(inviteVariables?: Record<string, string>): Promise<{ session: GameSession; player: Player }> {
    const params = inviteVariables && Object.keys(inviteVariables).length > 0
      ? new URLSearchParams(inviteVariables as Record<string, string>)
      : undefined;
    const url = params ? `/api/sessions/system?${params.toString()}` : '/api/sessions/system';
    const response = await this.axiosInstance.get<ApiResponse<{ session: GameSession; player: Player }>>(url);
    if (!response.data.data?.session || !response.data.data?.player) {
      throw new Error('Failed to get system session');
    }
    return {
      session: response.data.data.session,
      player: response.data.data.player
    };
  }

  async createSession(data: CreateSessionRequest): Promise<{ session: GameSession; players: Player[] }> {
    const response = await this.axiosInstance.post<ApiResponse<SessionResponse>>('/api/sessions', data);
    if (!response.data.data?.session) {
      throw new Error('Failed to create session');
    }
    // Backend returns 'player' (singular), convert to 'players' array for consistency
    const player = (response.data.data as any).player;
    return {
      session: response.data.data.session,
      players: player ? [player] : []
    };
  }

  /**
   * Create a guest session (for unauthenticated users)
   * @param adventureId - Adventure ID (must be system adventure)
   * @param guestUserId - Optional existing guest user ID from localStorage
   * @param invitePayload - Optional invite link payload (variables to set when opening the link)
   * @returns Session, player, tokens, and guest user ID
   */
  async createGuestSession(
    adventureId: string,
    guestUserId?: string,
    invitePayload?: { variables?: Record<string, string> }
  ): Promise<GuestSessionResponse> {
    // Get existing guest user ID if not provided
    const existingGuestData = guestService.getGuestData();
    const finalGuestUserId = guestUserId || existingGuestData?.userId;
    const linkVariables =
      invitePayload?.variables && Object.keys(invitePayload.variables).length > 0
        ? invitePayload.variables
        : undefined;

    console.log('[ApiService] createGuestSession called', {
      adventureId,
      guestUserId: finalGuestUserId,
      hasExistingGuestData: !!existingGuestData,
      hasLinkVariables: !!linkVariables,
    });

    // Make request without authentication (optionalAuth middleware)
    // Note: Use this.axiosInstance to ensure the interceptor handles token selection correctly
    // For guest session creation, the interceptor will not add regular tokens
    const response = await this.axiosInstance.post<ApiResponse<GuestSessionResponse>>(
      '/api/sessions/guest',
      {
        adventureId,
        ...(finalGuestUserId && { guestUserId: finalGuestUserId }),
        ...(linkVariables && { linkVariables }),
      }
    ).catch((error) => {
      console.error('[ApiService] createGuestSession failed:', error);
      console.error('[ApiService] Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers
        }
      });
      throw error;
    });

    if (!response.data.data) {
      throw new Error('Failed to create guest session');
    }

    const { session, player, token, refreshToken, guestUserId: returnedGuestUserId } = response.data.data;

    // Store guest tokens in localStorage via guestService
    guestService.clearGuestData(); // Clear old data first
    const guestData = {
      userId: returnedGuestUserId,
      token,
      refreshToken,
    };
    localStorage.setItem('guestUserId', guestData.userId);
    localStorage.setItem('guestToken', guestData.token);
    localStorage.setItem('guestRefreshToken', guestData.refreshToken);

    // Set token in auth service for subsequent API calls
    // Note: We need to handle guest tokens separately from regular tokens
    // For now, we'll set it in authService but mark it as guest
    // The auth store will need to handle this distinction

    return {
      session,
      player,
      token,
      refreshToken,
      guestUserId: returnedGuestUserId,
    };
  }

  /**
   * Redeem a guest adventure invite token for guest auth + pre-created session access.
   */
  async redeemGuestAdventure(token: string): Promise<GuestSessionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<GuestSessionResponse>>(
      '/api/guest-adventures/redeem',
      { token: token.trim() },
      { timeout: 15000 }
    );

    if (!response.data.data) {
      throw new Error('Failed to redeem guest adventure invite');
    }

    const { session, player, token: accessToken, refreshToken, guestUserId } = response.data.data;

    guestService.clearGuestData();
    guestService.saveGuestData({
      userId: guestUserId,
      token: accessToken,
      refreshToken,
    });

    return {
      session,
      player,
      token: accessToken,
      refreshToken,
      guestUserId,
    };
  }

  /**
   * Refresh guest token
   */
  async refreshGuestToken(refreshToken: string): Promise<{ token: string; refreshToken: string }> {
    const response = await axios.post<ApiResponse<{ token: string; refreshToken: string }>>(
      `${config.apiBaseUrl}/api/auth/refresh`,
      { refreshToken }
    );

    if (!response.data.data) {
      throw new Error('Invalid refresh response');
    }

    return response.data.data;
  }

  async getSession(sessionId: string): Promise<GameSession> {
    const response = await this.axiosInstance.get<ApiResponse<SessionResponse>>(`/api/sessions/${sessionId}`);
    if (!response.data.data?.session) {
      throw new Error('Session not found');
    }
    return response.data.data.session;
  }

  async joinSession(sessionId: string, playerSlot: string): Promise<Player> {
    // Get current user ID from auth store
    const authState = useAuthStore.getState();
    if (!authState.user?.id) {
      throw new Error('User must be logged in to join a session');
    }

    // Note: userId is sent via authentication token, not in request body
    // playerSlot is required - specifies which player slot to join.
    const clientOffsetMinutes = -new Date().getTimezoneOffset();
    const response = await this.axiosInstance.post<ApiResponse<PlayerResponse>>(
      `/api/sessions/${sessionId}/join`,
      { playerSlot, timeZoneOffsetMinutes: clientOffsetMinutes }
    );
    if (!response.data.data?.player) {
      throw new Error('Failed to join session');
    }
    return response.data.data.player;
  }

  async startSession(sessionId: string): Promise<void> {
    await this.axiosInstance.post(`/api/sessions/${sessionId}/start`);
  }

  async getSessionState(sessionId: string): Promise<GameStateResponse> {
    const response = await this.axiosInstance.get<ApiResponse<GameStateResponse>>(`/api/sessions/${sessionId}/state`);
    if (!response.data.data) {
      throw new Error('Failed to get session state');
    }
    return response.data.data;
  }

  async listPlaythroughSnapshots(sessionId: string): Promise<PlaythroughSnapshotListResult> {
    const response = await this.axiosInstance.get<ApiResponse<PlaythroughSnapshotListResult>>(
      `/api/sessions/${sessionId}/playthrough-snapshots`
    );
    if (!response.data.data) {
      throw new Error('Failed to list playthrough snapshots');
    }
    return response.data.data;
  }

  async loadPlaythroughSnapshot(
    sessionId: string,
    snapshotPath: string
  ): Promise<PlaythroughSnapshotEngineRestoreResult> {
    const response = await this.axiosInstance.post<ApiResponse<PlaythroughSnapshotEngineRestoreResult>>(
      `/api/sessions/${sessionId}/load-playthrough-snapshot`,
      { snapshotPath }
    );
    if (!response.data.data) {
      throw new Error('Failed to load playthrough snapshot');
    }
    return response.data.data;
  }

  async getSessionStats(sessionId: string): Promise<any> {
    const response = await this.axiosInstance.get<ApiResponse<any>>(`/api/sessions/${sessionId}/stats`);
    return response.data.data;
  }

  async deleteSession(sessionId: string): Promise<void> {
    await this.axiosInstance.delete(`/api/sessions/${sessionId}`);
  }

  async closeSession(sessionId: string): Promise<void> {
    await this.axiosInstance.patch(`/api/sessions/${sessionId}/close`);
  }

  async removePlayer(sessionId: string, playerId: string): Promise<void> {
    await this.axiosInstance.delete(`/api/sessions/${sessionId}/players/${playerId}`);
  }

  async leaveSession(sessionId: string): Promise<void> {
    // Note: This method should NOT remove the player from the database.
    // Players should persist regardless of user connection status.
    // This method is kept for API compatibility but does nothing.
    // The WebSocket disconnection is handled separately in the WebSocket layer.
    console.log(`User leaving session ${sessionId} - player data preserved`);
  }

  // Get user's active sessions (sessions where adventure has started)
  async getActiveSessions(): Promise<GameSession[]> {
    const response = await this.axiosInstance.get<ApiResponse<{ sessions: GameSession[] }>>('/api/users/active-sessions');
    return response.data.data?.sessions || [];
  }

  async getUserInfo(userId: string): Promise<{ userId: string; username: string; playerName?: string; portraitId?: string }> {
    const response = await this.axiosInstance.get<ApiResponse<{ userId: string; username: string; playerName?: string; portraitId?: string }>>(`/api/users/${userId}/info`);
    if (!response.data.data) {
      throw new Error('Failed to get user info');
    }
    return response.data.data;
  }

  // Player management
  async getCurrentScene(sessionId: string, playerId: string): Promise<SceneResponse> {
    const response = await this.axiosInstance.get<ApiResponse<SceneResponse>>(`/api/sessions/${sessionId}/players/${playerId}/current-scene`);
    if (!response.data.data) {
      throw new Error('Failed to get current scene');
    }
    return response.data.data;
  }

  async getAvailableOptions(sessionId: string, playerId: string): Promise<any[]> {
    const response = await this.axiosInstance.get<ApiResponse<{ options: any[] }>>(`/api/sessions/${sessionId}/players/${playerId}/options`);
    return response.data.data?.options || [];
  }

  async performAction(
    sessionId: string,
    playerId: string,
    action: PlayerActionRequest,
    options?: { timeoutMs?: number }
  ): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(
      `/api/sessions/${sessionId}/players/${playerId}/actions`,
      action,
      options?.timeoutMs !== undefined ? { timeout: options.timeoutMs } : undefined
    );
    if (!response.data.data) {
      throw new Error('Failed to perform action');
    }
    return response.data.data;
  }

  async querySharedVariables(
    sessionId: string,
    queries: Array<{ variableName: string; aggregation: string }>,
    content?: RichTextElement[]
  ): Promise<SharedVariablesQueryResults> {
    const response = await this.axiosInstance.post<
      ApiResponse<SharedVariablesQueryResults>
    >(
      `/api/sessions/${sessionId}/shared-variables/query`,
      { queries, content },
      { timeout: 10000 }
    );
    if (!response.data.data?.results) {
      throw new Error('Failed to query shared variables');
    }
    return response.data.data;
  }

  async submitUserInput(
    sessionId: string,
    playerId: string,
    payload: {
      variableName: string;
      value?: string;
      maxLength?: number;
    }
  ): Promise<UserInputActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<UserInputActionResponse>>(
      `/api/sessions/${sessionId}/players/${playerId}/actions`,
      {
        action: 'submit_user_input',
        variableName: payload.variableName,
        value: payload.value ?? '',
        ...(payload.maxLength !== undefined ? { maxLength: payload.maxLength } : {})
      }
    );

    if (!response.data.data) {
      throw new Error('Failed to submit user input');
    }

    return response.data.data;
  }

  async selectOption(sessionId: string, playerId: string, optionId: string): Promise<void> {
    const response = await this.axiosInstance.post(`/api/sessions/${sessionId}/players/${playerId}/select-option`, {
      optionId
    });
    if (!response.data.success) {
      throw new Error('Failed to select option');
    }
  }

  async selectFinishOption(sessionId: string, playerId: string): Promise<void> {
    const response = await this.axiosInstance.post<ApiResponse<{ gameState: any }>>(
      `/api/sessions/${sessionId}/players/${playerId}/finish`
    );
    if (!response.data.success) {
      throw new Error('Failed to complete adventure');
    }
  }

  async selectLeaveOption(sessionId: string, playerId: string): Promise<void> {
    const response = await this.axiosInstance.post<ApiResponse<{ gameState: any }>>(
      `/api/sessions/${sessionId}/players/${playerId}/leave`
    );
    if (!response.data.success) {
      throw new Error('Failed to leave adventure');
    }
  }

  async selectInventoryItem(sessionId: string, playerId: string, itemTag: string, targetScene: string): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(`/api/sessions/${sessionId}/players/${playerId}/select-inventory-item`, {
      itemTag,
      targetScene
    });
    if (!response.data.data) {
      throw new Error('Failed to select inventory item');
    }
    return response.data.data;
  }

  async selectActivityReference(
    sessionId: string,
    playerId: string,
    activityId: string,
    variableName: string,
    targetScene: string
  ): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(
      `/api/sessions/${sessionId}/players/${playerId}/select-activity-reference`,
      {
        activityId,
        variableName,
        targetScene
      }
    );
    if (!response.data.data) {
      throw new Error('Failed to select activity reference');
    }
    return response.data.data;
  }

  async visitEntity(sessionId: string, playerId: string, entityId: string): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(
      `/api/sessions/${sessionId}/players/${playerId}/visit-entity`,
      { entityId }
    );
    if (!response.data.data) {
      throw new Error('Failed to visit entity');
    }
    return response.data.data;
  }

  async reorderEntity(
    sessionId: string,
    playerId: string,
    entityId: string,
    direction: 'up' | 'down'
  ): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(
      `/api/sessions/${sessionId}/players/${playerId}/reorder-entity`,
      { entityId, direction }
    );
    if (!response.data.data) {
      throw new Error('Failed to reorder entity');
    }
    return response.data.data;
  }

  async selectEntityReference(
    sessionId: string,
    playerId: string,
    entityId: string,
    variableName: string,
    targetScene: string
  ): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(
      `/api/sessions/${sessionId}/players/${playerId}/select-entity-reference`,
      {
        entityId,
        variableName,
        targetScene
      }
    );
    if (!response.data.data) {
      throw new Error('Failed to select entity reference');
    }
    return response.data.data;
  }

  async listGuestAdventures(sessionId: string) {
    const response = await this.axiosInstance.get<
      ApiResponse<{
        invites: Array<{
          inviteId: string;
          guestUserId: string;
          adventureId: string;
          guestSessionId: string;
          initialVariables?: Record<string, string>;
          status: 'active' | 'expired' | 'revoked';
          createdAt: string;
          redeemedAt?: string | null;
        }>;
      }>
    >(`/api/sessions/${sessionId}/guest-adventures`);
    if (!response.data.data?.invites) {
      throw new Error('Failed to list guest adventures');
    }
    return response.data.data.invites;
  }

  async selectGuestAdventureInvite(
    sessionId: string,
    playerId: string,
    inviteId: string,
    variableName: string,
    targetScene: string
  ): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(
      `/api/sessions/${sessionId}/players/${playerId}/select-guest-adventure-invite`,
      {
        inviteId,
        variableName,
        targetScene
      }
    );
    if (!response.data.data) {
      throw new Error('Failed to select guest adventure invite');
    }
    return response.data.data;
  }

  async selectKeyedListEntry(
    sessionId: string,
    playerId: string,
    entryId: string,
    sourceVariable: string,
    variableName: string,
    targetScene: string
  ): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(
      `/api/sessions/${sessionId}/players/${playerId}/select-keyed-list-entry`,
      {
        entryId,
        sourceVariable,
        variableName,
        targetScene
      }
    );
    if (!response.data.data) {
      throw new Error('Failed to select keyed list entry');
    }
    return response.data.data;
  }

  async selectColorOption(
    sessionId: string,
    playerId: string,
    variableName: string,
    colorValue: string,
    targetScene: string
  ): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(
      `/api/sessions/${sessionId}/players/${playerId}/select-color-option`,
      {
        variableName,
        colorValue,
        targetScene
      }
    );
    if (!response.data.data) {
      throw new Error('Failed to select color option');
    }
    return response.data.data;
  }

  async selectScheduleEvent(sessionId: string, playerId: string, eventId: string, targetScene: string): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(`/api/sessions/${sessionId}/players/${playerId}/select-schedule-event`, {
      eventId,
      targetScene
    });
    if (!response.data.data) {
      throw new Error('Failed to select schedule event');
    }
    return response.data.data;
  }

  async selectScheduleEventTime(
    sessionId: string,
    playerId: string,
    eventId: string,
    timeEpochSeconds: number,
    targetScene: string,
    realWorldLocation?: string
  ): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(`/api/sessions/${sessionId}/players/${playerId}/select-schedule-event-time`, {
      eventId,
      timeEpochSeconds,
      targetScene,
      realWorldLocation
    });
    if (!response.data.data) {
      throw new Error('Failed to select schedule event time');
    }
    return response.data.data;
  }

  async selectSyncOption(
    sessionId: string,
    playerId: string,
    syncVar: string,
    targetScene: string,
    characterNames: string[],
    targetByCharacter?: Record<string, string>,
    syncEffects?: string
  ): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(`/api/sessions/${sessionId}/players/${playerId}/select-sync-option`, {
      syncVar,
      targetScene,
      characterNames,
      ...(targetByCharacter && Object.keys(targetByCharacter).length > 0 && { targetByCharacter }),
      ...(syncEffects && syncEffects.trim() !== '' && { syncEffects: syncEffects.trim() })
    });
    if (!response.data.data) {
      throw new Error('Failed to select sync option');
    }
    return response.data.data;
  }

  async selectFeedbackCard(sessionId: string, playerId: string, feedbackId: string, targetScene: string): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(
      `/api/sessions/${sessionId}/players/${playerId}/select-feedback-card`,
      {
        feedbackId,
        targetScene
      }
    );
    if (!response.data.data) {
      throw new Error('Failed to select feedback card');
    }
    return response.data.data;
  }

  async navigateToScene(
    sessionId: string,
    playerId: string,
    targetScene: string,
    options?: { recordReturnScene?: boolean }
  ): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(`/api/sessions/${sessionId}/players/${playerId}/navigate`, {
      targetScene,
      ...(options?.recordReturnScene === false ? { recordReturnScene: false } : {})
    });
    if (!response.data.data) {
      throw new Error('Failed to navigate to scene');
    }
    return response.data.data;
  }

  async navigateToActivityRedo(
    sessionId: string,
    playerId: string,
    targetScene: string,
    activityId: string
  ): Promise<ActionResponse> {
    const response = await this.axiosInstance.post<ApiResponse<ActionResponse>>(
      `/api/sessions/${sessionId}/players/${playerId}/navigate-activity-redo`,
      { targetScene, activityId }
    );
    if (!response.data.data) {
      throw new Error('Failed to navigate activity redo');
    }
    return response.data.data;
  }

  async getSessionPlayers(sessionId: string): Promise<PlayerWithUsername[]> {
    const response = await this.axiosInstance.get<ApiResponse<{ players: PlayerWithUsername[] }>>(`/api/sessions/${sessionId}/players`);
    return response.data.data?.players || [];
  }

  async getSessionAdventureVariable(
    sessionId: string,
    variableName: string
  ): Promise<string | null> {
    const response = await this.axiosInstance.get<
      ApiResponse<{ variableName: string; value: string | null }>
    >(`/api/sessions/${sessionId}/adventure-variables/${encodeURIComponent(variableName)}`);
    const value = response.data.data?.value;
    return value === undefined ? null : value;
  }

  /**
   * Read one adventure variable for many of the current user's sessions in one request.
   * Sessions the user does not belong to are omitted from the result.
   */
  async getSessionAdventureVariables(
    sessionIds: string[],
    variableName: string
  ): Promise<Record<string, string | null>> {
    if (sessionIds.length === 0) {
      return {};
    }
    const response = await this.axiosInstance.post<
      ApiResponse<{ values: Record<string, string | null> }>
    >('/api/users/adventure-variables', { sessionIds, variableName });
    return response.data.data?.values ?? {};
  }

  async getPlayer(sessionId: string, playerId: string): Promise<Player> {
    const response = await this.axiosInstance.get<ApiResponse<PlayerResponse>>(`/api/sessions/${sessionId}/players/${playerId}`);
    if (!response.data.data?.player) {
      throw new Error('Player not found');
    }
    return response.data.data.player;
  }

  async updatePlayerState(sessionId: string, playerId: string, updates: any): Promise<Player> {
    const response = await this.axiosInstance.put<ApiResponse<PlayerResponse>>(`/api/sessions/${sessionId}/players/${playerId}`, updates);
    if (!response.data.data?.player) {
      throw new Error('Failed to update player state');
    }
    return response.data.data.player;
  }

  async addItem(sessionId: string, playerId: string, item: string): Promise<Player> {
    const response = await this.axiosInstance.post<ApiResponse<PlayerResponse>>(`/api/sessions/${sessionId}/players/${playerId}/inventory`, { item });
    if (!response.data.data?.player) {
      throw new Error('Failed to add item');
    }
    return response.data.data.player;
  }

  async removeItem(sessionId: string, playerId: string, item: string): Promise<Player> {
    const response = await this.axiosInstance.delete<ApiResponse<PlayerResponse>>(`/api/sessions/${sessionId}/players/${playerId}/inventory/${encodeURIComponent(item)}`);
    if (!response.data.data?.player) {
      throw new Error('Failed to remove item');
    }
    return response.data.data.player;
  }

  async addStat(sessionId: string, playerId: string, stat: string): Promise<Player> {
    const response = await this.axiosInstance.post<ApiResponse<PlayerResponse>>(`/api/sessions/${sessionId}/players/${playerId}/stats`, { stat });
    if (!response.data.data?.player) {
      throw new Error('Failed to add stat');
    }
    return response.data.data.player;
  }

  async removeStat(sessionId: string, playerId: string, stat: string): Promise<Player> {
    const response = await this.axiosInstance.delete<ApiResponse<PlayerResponse>>(`/api/sessions/${sessionId}/players/${playerId}/stats/${encodeURIComponent(stat)}`);
    if (!response.data.data?.player) {
      throw new Error('Failed to remove stat');
    }
    return response.data.data.player;
  }

  // Location management
  async getAdventureLocations(adventureId: string): Promise<any[]> {
    const response = await this.axiosInstance.get<ApiResponse<{ locations: any[] }>>(this.adventureApiPath(adventureId, 'locations'));
    return response.data.data?.locations || [];
  }

  async getAdventureLocation(adventureId: string, locationTag: string): Promise<any> {
    const response = await this.axiosInstance.get<ApiResponse<{ location: any }>>(this.adventureApiPath(adventureId, `locations/${encodeURIComponent(locationTag)}`));
    if (!response.data.data?.location) {
      throw new Error('Location not found');
    }
    return response.data.data.location;
  }

  // Unified inventory management
  async getAllInventories(sessionId: string): Promise<any[]> {
    const response = await this.axiosInstance.get<ApiResponse<{ inventories: any[] }>>(`/api/sessions/${sessionId}/inventories`);
    return response.data.data?.inventories || [];
  }

  async getInventoryItems(sessionId: string, ownerId: string, ownerType: 'character' | 'location'): Promise<{ items: AdventureItem[] }> {
    const response = await this.axiosInstance.get<ApiResponse<{ items: AdventureItem[] }>>(`/api/sessions/${sessionId}/inventories/${encodeURIComponent(ownerId)}/${ownerType}`);
    if (!response.data.data?.items) {
      throw new Error('Failed to fetch inventory items');
    }
    return response.data.data;
  }

  async addItemToInventory(sessionId: string, ownerId: string, ownerType: 'character' | 'location', itemTag: string): Promise<void> {
    await this.axiosInstance.post(`/api/sessions/${sessionId}/inventories/${encodeURIComponent(ownerId)}/${ownerType}/items`, {
      itemTag
    });
  }

  async removeItemFromInventory(sessionId: string, ownerId: string, ownerType: 'character' | 'location', itemTag: string): Promise<void> {
    await this.axiosInstance.delete(`/api/sessions/${sessionId}/inventories/${encodeURIComponent(ownerId)}/${ownerType}/items/${encodeURIComponent(itemTag)}`);
  }

  // User cleanup methods
  async cleanupUserAdventures(email: string): Promise<{ cleanedCount: number; userEmail: string }> {
    const response = await this.axiosInstance.post<ApiResponse<{ cleanedCount: number; userEmail: string }>>('/api/test/cleanup-user-adventures', {
      email
    });
    if (!response.data.data) {
      throw new Error('Failed to cleanup user adventures');
    }
    return response.data.data;
  }

  // Utility methods
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.axiosInstance.get('/health');
      return response.status === 200;
    } catch {
      return false;
    }
  }

  // Retry logic for failed requests
  async retryRequest<T>(requestFn: () => Promise<T>, maxRetries: number = 3): Promise<T> {
    let lastError: Error;
    
    for (let i = 0; i < maxRetries; i++) {
      try {
        return await requestFn();
      } catch (error) {
        lastError = error as Error;
        if (i < maxRetries - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
        }
      }
    }
    
    throw lastError!;
  }
}

// Create singleton instance
const apiService = new ApiService();

export default apiService;

